import { Link } from "react-router-dom";
import { ArrowRight, Check, CheckCircle2, MonitorPlay, FileText, CalendarDays, BarChart3 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { HeroCarousel } from "@/components/site/HeroCarousel";
import { AssistantsCarousel } from "@/components/site/AssistantsCarousel";
import { PricingCards } from "@/components/site/PricingCards";
import { Reveal, SectionHead, StatusPill } from "@/components/site/Section";
import { FEATURES, STEPS, PROBLEMS, AUDIENCE, FAQS, INTEGRATIONS, CREDIT_POINTS, IMG } from "@/data/content";
import { AgentSamples, ClientsMarquee } from "@/components/site/HomeExtras";
import { useSiteContent } from "@/context/SiteContentContext";

const Marquee = () => (
  <div className="border-b bg-card/50 overflow-hidden py-5" data-testid="integrations-marquee">
    <div className="flex w-max animate-marquee gap-12">
      {[...INTEGRATIONS, ...INTEGRATIONS].map((x, i) => (
        <span key={i} className="flex items-center gap-3 font-display text-lg font-semibold text-foreground/50 whitespace-nowrap">
          <span className="h-1.5 w-1.5 rounded-full bg-[#7c3aed]" />{x.name}
        </span>
      ))}
    </div>
  </div>
);

const Problem = () => (
  <section className="py-20 md:py-28">
    <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
      <Reveal className="lg:col-span-5 relative">
        <img src={IMG.founders} alt="Small business team" className="aspect-[4/5] w-full rounded-[2rem] object-cover border" />
        <div className="absolute -bottom-6 -right-4 md:-right-8 glass rounded-2xl p-5 shadow-2xl max-w-[240px]">
          <div className="font-display text-3xl font-extrabold text-gradient">1 workspace</div>
          <div className="text-xs text-muted-foreground mt-1">instead of juggling many disconnected tools</div>
        </div>
      </Reveal>
      <div className="lg:col-span-7 lg:pl-8">
        <SectionHead eyebrow="The problem we solve" title="Marketing Shouldn't Require an Entire Department"
          copy="Growing a business takes time. Marketing often means juggling multiple tools, creating content from scratch, planning campaigns, managing social channels, and trying to understand what is working. Sliance brings these activities into one streamlined workspace, helping you:" />
        <div className="mt-8 grid sm:grid-cols-2 gap-3">
          {PROBLEMS.map((p, i) => (
            <Reveal key={p} delay={i * 0.05}>
              <div className="flex items-start gap-3 rounded-xl border bg-card p-4"><CheckCircle2 className="h-5 w-5 shrink-0 text-[#10b981]" /><span className="text-sm">{p}</span></div>
            </Reveal>
          ))}
        </div>
        <p className="mt-8 font-display text-xl font-semibold">Spend less time coordinating marketing and more time growing your business.</p>
      </div>
    </div>
  </section>
);

const Features = () => (
  <section className="py-20 md:py-28 bg-card/40 border-y relative overflow-hidden">
    <div className="container-x">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <SectionHead eyebrow="Explore the Sliance platform" title="Everything You Need to Organize Your Marketing" />
        <Link to="/tour" data-testid="features-tour-btn" className="btn-ghost shrink-0">Take the Product Tour <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="mt-14 grid gap-5 md:grid-cols-6">
        {FEATURES.map((f, i) => (
          <Reveal key={f.slug} delay={i * 0.06} className={i === 0 || i === 5 ? "md:col-span-4" : "md:col-span-2"}>
            <Link to={`/platform#${f.slug}`} data-testid={`feature-card-${f.slug}`} className="group card-box lift block h-full overflow-hidden">
              {(i === 0 || i === 5) && <img src={f.img} alt="" className="h-44 w-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />}
              <div className="p-7">
                <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: `${f.color}1f`, color: f.color }}><f.icon className="h-5 w-5" /></div>
                <h3 className="mt-5 text-xl font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.copy}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: f.color }}>{f.cta} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
      <p className="mt-8 text-xs text-muted-foreground">Feature availability depends on the selected plan and connected services. Features in development are labelled.</p>
    </div>
  </section>
);

const How = () => (
  <section className="py-20 md:py-28">
    <div className="container-x">
      <SectionHead eyebrow="How it works" title="From Business Goals to Organized Marketing" />
      <div className="mt-14 grid gap-5 md:grid-cols-5">
        {STEPS.map((s, i) => (
          <Reveal key={s.t} delay={i * 0.08}>
            <div className="relative card-box h-full p-6 lift" data-testid={`how-step-${i + 1}`}>
              <div className="font-mono text-xs text-[#7c3aed] dark:text-[#a78bfa]">STEP 0{i + 1}</div>
              <div className="mt-4 h-1 w-10 rounded-full bg-brand" />
              <h3 className="mt-5 text-lg font-semibold leading-snug">{s.t}</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Link to="/how-it-works" data-testid="how-cta" className="btn-ghost mt-10">See How Sliance Works <ArrowRight className="h-4 w-4" /></Link>
    </div>
  </section>
);

const Assistants = () => (
  <section className="relative py-20 md:py-28 bg-[#0b0f19] text-white overflow-hidden grain">
    <div className="absolute -top-32 right-10 h-96 w-96 rounded-full bg-[#db2777]/25 blur-[120px]" />
    <div className="absolute bottom-0 -left-20 h-80 w-80 rounded-full bg-[#4f46e5]/30 blur-[120px]" />
    <div className="container-x relative dark">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 text-white">
        <div className="max-w-3xl">
          <div className="eyebrow mb-4 !text-[#a78bfa]">Meet your AI marketing assistants</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08]">Specialized AI Assistance for Different Marketing Tasks</h2>
          <p className="mt-5 text-white/60">Capabilities are labelled by real status: Live, Beta or on the Roadmap.</p>
        </div>
        <Link to="/platform" data-testid="assistants-cta" className="btn-brand shrink-0">Discover AI Marketing Capabilities</Link>
      </div>
      <div className="mt-14 text-foreground"><AssistantsCarousel /></div>
    </div>
  </section>
);

const SeeBefore = () => (
  <section className="py-20 md:py-28">
    <div className="container-x grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <SectionHead eyebrow="See before you subscribe" title="Explore the Experience Before Making a Decision"
          copy="You should know what you are paying for. Explore sample workflows and outputs before selecting a plan." />
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/tour" data-testid="see-demo-btn" className="btn-brand">Launch Interactive Demo</Link>
          <Link to="/tour#samples" data-testid="see-plan-btn" className="btn-ghost">View Sample Marketing Plan</Link>
        </div>
        <p className="mt-5 text-xs text-muted-foreground">Demo data is illustrative and not your own live business data.</p>
      </div>
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
        {[[MonitorPlay, "Interactive Product Tour", "Navigate a guided sample workspace.", "#4f46e5"], [FileText, "Sample AI Content", "View example social posts, blog drafts, and campaign copy.", "#db2777"], [CalendarDays, "Sample Content Calendar", "See how a month of marketing activity can be organized.", "#7c3aed"], [BarChart3, "Sample Marketing Report", "Explore how performance information can be presented.", "#10b981"]].map(([I, t, d, c], i) => (
          <Reveal key={t} delay={i * 0.07}>
            <Link to="/tour" className="card-box lift block p-7 h-full" data-testid={`see-card-${i}`}>
              <I className="h-7 w-7" style={{ color: c }} />
              <h3 className="mt-5 text-lg font-semibold">{t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{d}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const IntegrationsGrid = () => (
  <section className="py-20 md:py-28 bg-card/40 border-y">
    <div className="container-x grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <SectionHead eyebrow="Integrations" title="Work With the Tools You Already Use"
          copy="Use supported integrations to streamline your workflow while keeping control over your content, approvals, and publishing decisions." />
        <Link to="/integrations" data-testid="integrations-cta" className="btn-ghost mt-8">Explore Integrations <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-3">
        {INTEGRATIONS.slice(0, 12).map((x) => (
          <div key={x.name} className="rounded-xl border bg-card p-4 flex flex-col gap-3">
            <span className="text-sm font-semibold">{x.name}</span>
            <div className="flex items-center justify-between"><span className="text-[11px] text-muted-foreground">{x.cat}</span><StatusPill status={x.status} /></div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PricingPreview = () => (
  <section className="py-20 md:py-28" id="pricing">
    <div className="container-x">
      <SectionHead eyebrow="Pricing" title="Choose a Plan That Fits Your Business" copy="Start with the capabilities you need today and move to a higher plan as your marketing needs grow." />
      <div className="mt-12"><PricingCards /></div>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/pricing" data-testid="pricing-compare-btn" className="btn-brand">Compare All Plans</Link>
        <Link to="/tour" className="btn-ghost">Explore Before Subscribing</Link>
      </div>
    </div>
  </section>
);

const Credits = () => (
  <section className="py-20 md:py-24">
    <div className="container-x">
      <div className="relative overflow-hidden rounded-[2rem] bg-brand p-8 md:p-14 text-white grain">
        <div className="relative grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/70">Understand AI credits</div>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight">Flexible AI Usage. Clearer Costs.</h2>
            <p className="mt-4 text-white/80">Sliance uses AI credits to help you understand and manage AI usage — use your plan's included credits or purchase additional credit packs.</p>
            <Link to="/pricing#credits" data-testid="credits-cta" className="mt-8 inline-flex rounded-full bg-white px-6 py-3 text-sm font-semibold text-[#4f46e5] hover:-translate-y-0.5 transition-transform">Learn About AI Credits</Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
            {CREDIT_POINTS.map((c) => (
              <div key={c.t} className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 p-6">
                <c.icon className="h-6 w-6" /><h3 className="mt-4 font-semibold">{c.t}</h3><p className="mt-2 text-sm text-white/75">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Audience = () => (
  <section className="py-20 md:py-24">
    <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <SectionHead eyebrow="Built for growing businesses" title="Practical AI Marketing for Teams That Need to Move Faster"
          copy="Whether you are building your marketing process from scratch or improving an existing workflow, Sliance helps bring planning, creation, and measurement closer together." />
        <ul className="mt-8 space-y-3">
          {AUDIENCE.map((a) => <li key={a} className="flex items-center gap-3"><Check className="h-5 w-5 text-[#7c3aed]" /><span>{a}</span></li>)}
        </ul>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <img src={IMG.meeting} alt="" className="rounded-2xl aspect-square object-cover border" />
        <img src={IMG.analytics} alt="" className="rounded-2xl aspect-square object-cover border mt-10" />
      </div>
    </div>
  </section>
);

export const FaqList = ({ items }) => {
  const { content } = useSiteContent();
  items = items || (content?.faqs?.length ? content.faqs : FAQS);
  return (
  <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
    {items.map((f, i) => (
      <AccordionItem key={f.q} value={`f${i}`} className="border-b">
        <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-base font-semibold py-5 hover:no-underline">{f.q}</AccordionTrigger>
        <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
  );
};

const Faq = () => (
  <section className="py-20 md:py-28 bg-card/40 border-y" id="faqs">
    <div className="container-x grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4"><SectionHead eyebrow="FAQ" title="Frequently Asked Questions" /></div>
      <div className="lg:col-span-8"><FaqList /></div>
    </div>
  </section>
);

const FinalCta = () => (
  <section className="py-24 md:py-32 relative overflow-hidden">
    <div className="absolute inset-0 grid-lines opacity-50 [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]" />
    <div className="container-x relative">
      <Reveal>
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-4xl">Make Your Next Marketing Move With <span className="text-gradient">Sliance</span></h2>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl">Explore the platform, discover what AI can help you accomplish, and choose the right plan when you are ready.</p>
        <div className="mt-9 flex flex-wrap gap-3">
          <Link to="/platform" data-testid="final-explore-btn" className="btn-brand">Explore the Platform <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/pricing" data-testid="final-pricing-btn" className="btn-ghost">Compare Pricing</Link>
        </div>
        <p className="mt-5 text-sm text-muted-foreground">Browse first. Understand the features. Subscribe when it makes sense for your business.</p>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <HeroCarousel />
      <Marquee />
      <Problem />
      <Features />
      <How />
      <Assistants />
      <AgentSamples />
      <ClientsMarquee />
      <SeeBefore />
      <IntegrationsGrid />
      <PricingPreview />
      <Credits />
      <Audience />
      <Faq />
      <FinalCta />
    </div>
  );
}
